import * as React from 'react'
import { format, parseISO } from 'date-fns'
import type { WallClock } from '@lib/validators/temporal.validator'
import { fmt12, fmtCompact12, timeRangeLabel } from '@/lib/eventTime'
import { trackIdsOf } from '@lib/helpers/event-track'
import { ChevronLeft, ChevronRight, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import {
  addMonths,
  dayKeyOf,
  monthGrid,
  monthLabel,
  todayKey,
  type MonthDay,
  type MonthRef,
} from '@/lib/monthGrid'
import { byStartThenName } from '@/lib/venueAttention'
import type { CalendarEventDto, CalendarHoldDto, CalendarTrackDto } from '@lib/dto/venue-calendar.dto'

/** One chip in a day: a booking, or an inquiry's hold. */
type DayItem = { kind: 'event'; event: CalendarEventDto } | { kind: 'hold'; hold: CalendarHoldDto }

/** When a hold ends, as its tooltip says it: "Jun 1". */
function formatHoldUntil(day: CalendarHoldDto['endsOn']): string {
  return format(parseISO(day), 'MMM d')
}

/** Sunday-start, matching the date pickers elsewhere in the app. */
const WEEK_STARTS_ON = 0
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
/** How many chips a cell shows before it collapses the rest into a count. */
const CHIPS_PER_DAY = 3
/** How many data colors the palette offers. */
const DATA_COLORS = 8

interface VenueCalendarProps {
  month: MonthRef
  onMonthChange: (month: MonthRef) => void
  events: CalendarEventDto[]
  /** Inquiries' holds: drawn after the day's bookings, dashed, and never counted as one. */
  holds?: CalendarHoldDto[]
  tracks: CalendarTrackDto[]
  /** True at a venue partitioned into tracks — the only case where color means something. */
  concurrentEvents: boolean
  onSelectEvent: (eventId: string) => void
  loading?: boolean
}

/**
 * The palette slots for the lanes an event holds, in the venue's own order.
 *
 * **Empty is not "every lane".** An event with no `trackIds` may *use* every
 * room — `spaceOnTrack` says so — but it holds no lane, and the booking guard
 * agrees: `overlappingEvent` returns nothing for an empty set, so such an event
 * blocks nobody and can sit beside a lane booking at the same hour. Drawing it
 * as though it took all the lanes would claim the venue was taken when it is
 * not. It gets no slot, and the neutral chip, which is also what a venue with no
 * lanes to tell apart uses.
 *
 * Ordered by the venue's own track order rather than by the order the event's
 * ids arrived, so two events holding the same pair of lanes are drawn alike.
 */
function laneSlots(
  event: CalendarEventDto,
  trackIndex: Map<string, number>,
): number[] {
  return trackIdsOf(event)
    .map((id) => trackIndex.get(id))
    .filter((index): index is number => index !== undefined)
    .sort((a, b) => a - b)
    .map((index) => (index % DATA_COLORS) + 1)
}

/**
 * Which data color an event's chip wears.
 *
 * At a venue that runs concurrent events the color is its track, so two events
 * in one lane on one day are visible as a clash rather than as two chips. At a
 * single-track venue there is nothing to tell apart, and eight hues would be
 * decoration — every chip takes the neutral instead.
 *
 * An event on several lanes takes no single color: it gets the neutral ground
 * and {@link spanGradient} paints its bar. The chip used to color by the
 * *first* lane, which drew an event across the whole venue as if it were one
 * part of it.
 */
function chipColorClass(slots: number[], concurrentEvents: boolean): string {
  if (!concurrentEvents || slots.length !== 1) return 'ds-data-8'
  return `ds-data-${String(slots[0] ?? 1)}`
}

/**
 * The identity bar for an event across several lanes: one hard-stopped segment
 * per lane, top to bottom.
 *
 * Hard stops, not a blend — a blend would invent colors that are not any
 * lane's, and read as a ninth track.
 */
function spanGradient(slots: number[]): string {
  const stops = slots.flatMap((slot, i) => {
    const from = ((i / slots.length) * 100).toFixed(3)
    const to = (((i + 1) / slots.length) * 100).toFixed(3)
    return [`var(--ds-data-${String(slot)}-fill) ${from}%`, `var(--ds-data-${String(slot)}-fill) ${to}%`]
  })
  return `linear-gradient(to bottom, ${stops.join(', ')})`
}

/**
 * The start time as the venue keeps it, or nothing when no time is set.
 *
 * **Not the viewer's zone**, which is what this used to do and say it did. Event
 * times are wall clock — 22:00 means 22:00 at the venue — so converting them
 * showed a 10pm reception at 3pm to a coordinator in California and at 9am the
 * next morning to one in Sydney. `fmt12` reads the stored `HH:MM`; see
 * `eventTime.ts` and `docs/event-scheduling.md`.
 */
function timeLabel(startsAt: WallClock | null): string | null {
  if (startsAt === null) return null
  return fmt12(startsAt) || null
}

/**
 * The same time at chip size: `12p`, `6:30p`.
 *
 * A cell is about 120px wide, and "12:00 PM" spent nearly all of it — the name of
 * the event, which is the thing being looked for, was reduced to one letter. The
 * hour is what a glance needs; the full time is a hover away in the tooltip.
 */
function compactTimeLabel(startsAt: WallClock | null): string | null {
  // `getHours()` is the *viewer's* hours. These are the venue's.
  if (startsAt === null) return null
  return fmtCompact12(startsAt) || null
}

/**
 * Which lanes an event holds, named.
 *
 * **Because color cannot be the only carrier.** The legend maps a hue to a
 * lane, but a chip spanning three of them is asking a viewer to read a 3px bar
 * — so the tooltip says it in words. "Whole venue" rather than listing every
 * lane, because that is the fact worth knowing and a venue may have several.
 */
function laneLabel(
  event: CalendarEventDto,
  tracks: CalendarTrackDto[],
  concurrentEvents: boolean,
): string | null {
  // At a venue with nothing to partition, every event is on the only lane there
  // is. Saying so on every chip is noise, and the chip colors say nothing there
  // for the same reason.
  if (!concurrentEvents || tracks.length === 0) return null
  const held = trackIdsOf(event)
  // No lane at all is its own answer, and not "the whole venue" — it holds
  // nothing, so it is the one kind of event another booking can be beside.
  if (held.length === 0) return 'No track'
  if (held.length >= tracks.length) return 'Whole venue'
  const names = tracks.filter((t) => held.includes(t.id)).map((t) => t.name)
  return names.length > 0 ? names.join(' + ') : null
}

/**
 * True when the window runs past midnight into the following morning.
 *
 * Read off the two stored days rather than by comparing clocks: `endsAt` carries
 * the next day's date when a window crosses, which is the whole reason it is
 * stored that way — "ends earlier than it starts" was the rule that made a
 * 22:00–01:00 reception unrepresentable. See `eventWindow`.
 */
function crossesMidnight(event: CalendarEventDto): boolean {
  if (event.startsAt === null || event.endsAt === null) return false
  return event.endsAt.slice(0, 10) !== event.startsAt.slice(0, 10)
}

/** "Jordyn + Ben's Wedding · 4:00 PM – 1:00 AM (ends next day) · Whole venue" */
export function eventSummary(
  event: CalendarEventDto,
  tracks: CalendarTrackDto[],
  concurrentEvents = true,
): string {
  const parts = [event.name]
  // The range, not just the start: a chip sitting on the 14th for an event that
  // runs to 01:00 on the 15th has to be able to say so. It appears on the
  // starting day only — that is the decision in `docs/event-scheduling.md` —
  // which makes the end time the only thing that tells a reader it continues.
  const range = timeRangeLabel(event.startsAt, event.endsAt)
  const time = range === '' ? timeLabel(event.startsAt) : range
  if (time) parts.push(crossesMidnight(event) ? `${time} (ends next day)` : time)
  const lanes = laneLabel(event, tracks, concurrentEvents)
  if (lanes) parts.push(lanes)
  if (event.guestCount > 0) parts.push(`${String(event.guestCount)} guests`)
  const rooms = [...new Set(event.phases.map((p) => p.spaceName))]
  if (rooms.length > 0) parts.push(rooms.join(', '))
  if (event.unconfirmedPhaseCount > 0) {
    parts.push(
      event.unconfirmedPhaseCount === 1
        ? '1 plan not confirmed'
        : `${String(event.unconfirmedPhaseCount)} plans not confirmed`,
    )
  }
  return parts.join(' · ')
}

/**
 * A venue's month, as the venue reads it.
 *
 * The grid is the design system's `.ds-cal` recipe — six fixed weeks, so paging
 * does not make the page jump; chips in soft data colors rather than saturated
 * blocks, which stop being readable at chip size; today marked on the number
 * rather than by filling the cell, which would read as a selection.
 *
 * Every chip is a button: reading a calendar always ends in opening something.
 */
export function VenueCalendar({
  month,
  onMonthChange,
  events,
  holds = [],
  tracks,
  concurrentEvents,
  onSelectEvent,
  loading = false,
}: VenueCalendarProps) {
  const [expanded, setExpanded] = React.useState<string | null>(null)
  const cells = React.useMemo(() => monthGrid(month, WEEK_STARTS_ON), [month])
  const today = todayKey()

  const trackIndex = React.useMemo(
    () => new Map(tracks.map((track, index) => [track.id, index])),
    [tracks],
  )

  const byDay = React.useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>()
    for (const event of events) {
      const key = dayKeyOf(event.date)
      const list = map.get(key) ?? []
      list.push(event)
      map.set(key, list)
    }
    // Timed events first, in time order; then the untimed, which have no place in
    // a sequence and would otherwise jump around as the list re-renders.
    for (const list of map.values()) list.sort(byStartThenName)
    return map
  }, [events])

  const holdsByDay = React.useMemo(() => {
    const map = new Map<string, CalendarHoldDto[]>()
    for (const hold of holds) {
      const key = dayKeyOf(hold.date)
      map.set(key, [...(map.get(key) ?? []), hold])
    }
    return map
  }, [holds])

  function renderHoldChip(hold: CalendarHoldDto): React.ReactNode {
    const label = `Hold · ${hold.eventName}`
    const until = `held until ${formatHoldUntil(hold.endsOn)}`
    return (
      <Tooltip key={`hold-${hold.eventId}`} content={`${label} · ${until}`}>
        <button type="button" className="ds-cal-chip ds-cal-chip--hold" onClick={() => onSelectEvent(hold.eventId)}>
          <span className="ds-cal-chip-name">{label}</span>
        </button>
      </Tooltip>
    )
  }

  function renderChip(event: CalendarEventDto): React.ReactNode {
    const time = compactTimeLabel(event.startsAt)
    const slots = concurrentEvents ? laneSlots(event, trackIndex) : []
    const spans = slots.length > 1
    return (
      <Tooltip key={event.id} content={eventSummary(event, tracks, concurrentEvents)}>
        <button
          type="button"
          className={`ds-cal-chip ${chipColorClass(slots, concurrentEvents)}${spans ? ' ds-cal-chip--span' : ''}`}
          // Only the image, because only this knows which lanes. Everything
          // about how it is drawn lives in `.ds-cal-chip--span`.
          style={spans ? { backgroundImage: spanGradient(slots) } : undefined}
          onClick={() => onSelectEvent(event.id)}
        >
          {event.unconfirmedPhaseCount > 0 ? (
            <span className="ds-attention" aria-hidden="true">
              <TriangleAlert />
            </span>
          ) : null}
          {time ? <span className="ds-cal-chip-meta">{time}</span> : null}
          <span className="ds-cal-chip-name">{event.name}</span>
        </button>
      </Tooltip>
    )
  }

  function renderDay(cell: MonthDay): React.ReactNode {
    // Bookings first, then holds, so a busy day shows what is taken before what is spoken for.
    const dayItems: DayItem[] = [
      ...(byDay.get(cell.key) ?? []).map((event): DayItem => ({ kind: 'event', event })),
      ...(holdsByDay.get(cell.key) ?? []).map((hold): DayItem => ({ kind: 'hold', hold })),
    ]
    const showAll = expanded === cell.key
    const shown = showAll ? dayItems : dayItems.slice(0, CHIPS_PER_DAY)
    const hidden = dayItems.length - shown.length
    const isToday = cell.key === today
    return (
      <div key={cell.key} className={`ds-cal-day${cell.inMonth ? '' : ' ds-cal-day--outside'}`}>
        <span className="ds-cal-dayhead">
          {/* One badge, both parts: the mark says "this day is special" and the
              word inside it says which special. Two separate marks read as two
              things going on in the cell. */}
          <span className={`ds-cal-daynum${isToday ? ' ds-cal-daynum--today' : ''}`}>
            {cell.day}
            {isToday ? <span className="ds-cal-today-label">Today</span> : null}
          </span>
        </span>
        {shown.map((item) => (item.kind === 'event' ? renderChip(item.event) : renderHoldChip(item.hold)))}
        {hidden > 0 ? (
          <button type="button" className="ds-cal-more" onClick={() => setExpanded(cell.key)}>
            +{hidden} more
          </button>
        ) : null}
        {showAll && dayItems.length > CHIPS_PER_DAY ? (
          <button type="button" className="ds-cal-more" onClick={() => setExpanded(null)}>
            Show less
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Previous month"
            onClick={() => onMonthChange(addMonths(month, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="min-w-[9.5rem] text-center text-sm font-semibold">{monthLabel(month)}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Next month"
            onClick={() => onMonthChange(addMonths(month, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3">
          {loading ? <span className="text-xs text-ink-muted">Loading…</span> : null}
          {concurrentEvents && tracks.length > 0 ? (
            <ul className="flex flex-wrap items-center gap-3">
              {tracks.map((track, index) => (
                <li key={track.id} className="flex items-center gap-1.5 text-xs text-ink-muted">
                  <span className={`ds-data-${String((index % DATA_COLORS) + 1)} ds-cal-swatch`} />
                  {track.name}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="ds-cal">
        {WEEKDAYS.map((day) => (
          <div key={day} className="ds-cal-weekday">
            {day}
          </div>
        ))}
        {cells.map(renderDay)}
      </div>
    </div>
  )
}
